<template>
    <Spinner v-if="displaySpinner" />
    <Fluid class="field" v-if="!suggestions.length && !generatingSuggestions">
        <label for="name">Name</label>
        <InputText
            id="name"
            autofocus
            v-model="newName"
            required
            :value="newName"
        />
    </Fluid>
    <SuggestionsList
        :generatingSuggestions="generatingSuggestions"
        :suggestions="suggestions"
        :selectedSuggestions="selectedSuggestions"
        @update:selectedSuggestions="selectedSuggestions = $event"
    />
    <div class="buttons" v-if="!generatingSuggestions">
        <Button label="Cancel" text severity="secondary" @click="cancel" />
        <Button
            :label="
                suggestions.length
                    ? `Add ${selectedSuggestions.length} suggestions`
                    : 'Save'
            "
            severity="primary"
            @click="closeAndSave"
            :disabled="!!suggestions.length && !selectedSuggestions.length"
            v-tooltip="
                suggestions.length && !selectedSuggestions.length
                    ? 'Select at least one suggestion to add'
                    : ''
            "
        />
        <Button
            v-if="!suggestions.length && !generatingSuggestions"
            class="p-button-secondary"
            icon="pi pi-sparkles"
            label="Generate suggestions"
            @click="generateSuggestions"
            id="generateSuggestionsButton"
        />
    </div>
</template>

<script setup lang="ts">
import { Ref, computed, inject, ref } from "vue";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Spinner from "@/shared/Spinner.vue";
import {
    useProcessesManager,
    useProcessesStore,
} from "@/stores/processes-store";
import InputText from "primevue/inputtext";
import { useHotKeys } from "@/composables/hotkeys";
import { useBusinessEntitiesStore } from "@/stores/business-entities-store";
import SuggestionsList from "@/components/SuggestionsList.vue";

const newName = ref("");
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const { create } = useProcessesStore();
const { isLoading } = useProcessesManager();
const { addHotKey } = useHotKeys();
const { generateChildren } = useBusinessEntitiesStore();
const suggestions = ref<{ name: string | undefined; description: string }[]>(
    []
);
const selectedSuggestions = ref<number[]>([]);
const generatingSuggestions = ref<boolean>(false);

const cancel = () => dialogRef?.value.close();
const closeAndSave = async () => {
    if (newName.value) {
        await create(newName.value);
    } else if (selectedSuggestions.value.length) {
        for (const index of selectedSuggestions.value) {
            const suggestion = suggestions.value[index];
            const descriptionBlob = new Blob([suggestion.description], {
                type: "text/markdown",
            });
            await create(suggestion.name || "", undefined, descriptionBlob);
        }
    }

    dialogRef?.value?.close();
};

const displaySpinner = computed(() => {
    return isLoading.value;
});

/**
 * Save on cmd/ctrl + enter.
 */
addHotKey("CommandOrMeta+Enter", async () => {
    await closeAndSave();
});

const generateSuggestions = async () => {
    generatingSuggestions.value = true;
    suggestions.value = (await generateChildren()) || [];
    generatingSuggestions.value = false;
};
</script>

<style lang="scss" scoped>
.field {
    padding-bottom: 1em;

    label {
        display: block;
        margin-bottom: 0.5em;
    }
}

.buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5em;
    margin-top: 1em;
}
</style>
